import { gqlFetcher, graphql } from '#features/api';

/**
 * Граф связей текущего человека: четыре списка и счётчики.
 *
 * Все списки страничные по курсору `(createdAt, id)`, а не по offset: список подписчиков растёт
 * сверху и без участия человека, и offset на нём даёт и дубли, и пропуски.
 *
 * Порядок целиком с сервера — сначала ярус присутствия, потом время связи. Ярус «в сети» живёт
 * в памяти teamplay-ws, а не в базе связей, поэтому шлюз снимает у ws снимок онлайна и передаёт
 * `onlineIds` в запрос страницы; SQL сортирует уже с ним.
 *
 * Клиент порядок НЕ трогает — и не может: переставить он вправе только загруженные строки, а друг
 * в сети со старой связью лежит на третьей странице. Ровно так это и было сделано сначала, и
 * выглядело правильным ровно до второй страницы.
 */

const friendsDoc = graphql(`
  query Friends($limit: Int, $cursor: String, $presenceFilter: String) {
    friends(limit: $limit, cursor: $cursor, presenceFilter: $presenceFilter) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
      onlineCount
      playingCount
    }
  }
`);

const followingOnlyDoc = graphql(`
  query FollowingOnly($limit: Int, $cursor: String, $presenceFilter: String) {
    followingOnly(limit: $limit, cursor: $cursor, presenceFilter: $presenceFilter) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
      onlineCount
      playingCount
    }
  }
`);

const followingDoc = graphql(`
  query Following($limit: Int, $cursor: String) {
    following(limit: $limit, cursor: $cursor) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
    }
  }
`);

const followersDoc = graphql(`
  query Followers($limit: Int, $cursor: String) {
    followers(limit: $limit, cursor: $cursor) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
    }
  }
`);

const newFollowersDoc = graphql(`
  query NewFollowers($limit: Int, $cursor: String, $presenceFilter: String) {
    newFollowers(limit: $limit, cursor: $cursor, presenceFilter: $presenceFilter) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
      onlineCount
      playingCount
    }
  }
`);

/**
 * Кого я заблокировал. Форма ответа та же, что у списков подписок, — экран рисует его той же
 * строкой; `mutual` здесь всегда false, присутствие сервер не сортирует.
 */
const blockedUsersDoc = graphql(`
  query BlockedUsers($limit: Int, $cursor: String) {
    blockedUsers(limit: $limit, cursor: $cursor) {
      items {
        userId
        createdAt
        mutual
        userData {
          id
          userId
          displayName
          avatar
          country {
            icon
            name
          }
        }
      }
      nextCursor
    }
  }
`);

const followCountsDoc = graphql(`
  query FollowCounts {
    followCounts {
      friends
      following
      followers
      newFollowers
      unseenFollowers
      blocked
    }
  }
`);

const teammatesDoc = graphql(`
  query RecentTeammates($limit: Int) {
    recentTeammates(limit: $limit) {
      userId
      sessionsTogether
      lastPlayedAt
      lastGameId
      confirmedPlayed
      userData {
        id
        userId
        displayName
        avatar
        country {
          icon
          name
        }
      }
    }
  }
`);

export type FollowUser = {
  userId: string;
  displayName?: string | null;
  avatar?: string | null;
  /** Флаг рядом с ником: в LFG страна — это часовой пояс и язык, а не украшение. */
  country?: { icon?: string | null; name?: string | null } | null;
};

export type FollowEdge = {
  userId: string;
  createdAt: string;
  mutual: boolean;
  user: FollowUser | null;
};

export type FollowPage = {
  items: FollowEdge[];
  nextCursor: string | null;
  /** Цифры для чипов. Приходят только с первой страницей — дальше список тот же. */
  onlineCount?: number | null;
  playingCount?: number | null;
};

export type FollowCounts = {
  friends: number;
  following: number;
  followers: number;
  /**
   * Подписались на меня, я в ответ нет. Число у ярлыка вкладки «Подписчики» и её содержимое.
   * От просмотра НЕ гаснет: оно подписывает список, а список никуда не делся.
   */
  newFollowers: number;
  /**
   * Из них пришедшие после последнего открытия вкладки. Всё, что горит: точка на аватаре,
   * бейдж в меню, точка у вкладки. Гаснет от просмотра — это уведомление, а не список дел.
   */
  unseenFollowers: number;
  /** Кого я заблокировал — число на пятой вкладке. */
  blocked: number;
};

export type Teammate = {
  userId: string;
  sessionsTogether: number;
  lastPlayedAt?: string | null;
  lastGameId?: string | null;
  /**
   * Опрос после сбора подтвердил, что игра состоялась.
   *
   * ⚠️ Только при `true` подпись вправе сказать «играли вместе». Иначе — «были в одном сборе»:
   * место в сборе не означает игру, сбор мог не собраться. Ложная подпись здесь ломает ровно то
   * доверие, ради которого тёплый контекст и показывается.
   */
  confirmedPlayed: boolean;
  user: FollowUser | null;
};

type RawEdge = {
  userId?: string | null;
  createdAt?: string | null;
  mutual?: boolean | null;
  userData?: {
    userId?: string | null;
    displayName?: string | null;
    avatar?: string | null;
    country?: { icon?: string | null; name?: string | null } | null;
  } | null;
};

const toUser = (raw: RawEdge['userData'], fallbackId: string): FollowUser | null => {
  if (!raw) {
    return null;
  }
  return {
    userId: raw.userId ?? fallbackId,
    displayName: raw.displayName,
    avatar: raw.avatar,
    country: raw.country,
  };
};

const toPage = (
  raw:
    | {
        items?: (RawEdge | null)[] | null;
        nextCursor?: string | null;
        onlineCount?: number | null;
        playingCount?: number | null;
      }
    | null
    | undefined
): FollowPage => {
  const items = (raw?.items ?? [])
    .filter((edge): edge is RawEdge => Boolean(edge?.userId))
    .map((edge) => ({
      userId: edge.userId!,
      createdAt: edge.createdAt ?? '',
      mutual: Boolean(edge.mutual),
      user: toUser(edge.userData, edge.userId!),
    }));

  return {
    items,
    nextCursor: raw?.nextCursor ?? null,
    onlineCount: raw?.onlineCount,
    playingCount: raw?.playingCount,
  };
};

/**
 * Какой из четырёх списков читаем.
 *
 * Два из них — подмножества двух других: `friends` это взаимные внутри `following`,
 * `newFollowers` — неотвеченные внутри `followers`. Поэтому экран показывает вкладку
 * из большего списка, а меньший включается чипом, и одновременно живым остаётся ровно
 * один запрос.
 */
export type FollowListKind =
  | 'friends'
  | 'following'
  | 'followingOnly'
  | 'newFollowers'
  | 'followers'
  | 'blocked';

export const fetchFollowList = async (
  kind: FollowListKind,
  limit: number,
  cursor: string | null,
  presenceFilter: string | null = null
): Promise<FollowPage> => {
  if (kind === 'friends') {
    const data = await gqlFetcher(friendsDoc, { limit, cursor, presenceFilter });
    return toPage(data?.friends as never);
  }
  if (kind === 'followingOnly') {
    const data = await gqlFetcher(followingOnlyDoc, { limit, cursor, presenceFilter });
    return toPage(data?.followingOnly as never);
  }
  if (kind === 'following') {
    const data = await gqlFetcher(followingDoc, { limit, cursor });
    return toPage(data?.following as never);
  }
  if (kind === 'followers') {
    const data = await gqlFetcher(followersDoc, { limit, cursor });
    return toPage(data?.followers as never);
  }
  if (kind === 'blocked') {
    const data = await gqlFetcher(blockedUsersDoc, { limit, cursor });
    return toPage(data?.blockedUsers as never);
  }
  const data = await gqlFetcher(newFollowersDoc, { limit, cursor, presenceFilter });
  return toPage(data?.newFollowers as never);
};

export const fetchFollowCounts = async (): Promise<FollowCounts> => {
  const data = await gqlFetcher(followCountsDoc, {});
  const counts = data?.followCounts;

  return {
    friends: counts?.friends ?? 0,
    following: counts?.following ?? 0,
    followers: counts?.followers ?? 0,
    newFollowers: counts?.newFollowers ?? 0,
    unseenFollowers: counts?.unseenFollowers ?? 0,
    blocked: counts?.blocked ?? 0,
  };
};

export const fetchRecentTeammates = async (limit = 30): Promise<Teammate[]> => {
  const data = await gqlFetcher(teammatesDoc, { limit });

  return (data?.recentTeammates ?? [])
    .filter((row): row is NonNullable<typeof row> => Boolean(row?.userId))
    .map((row) => ({
      userId: row.userId!,
      sessionsTogether: row.sessionsTogether ?? 0,
      lastPlayedAt: row.lastPlayedAt,
      lastGameId: row.lastGameId,
      confirmedPlayed: Boolean(row.confirmedPlayed),
      user: toUser(row.userData as never, row.userId!),
    }));
};

/** Ключи запросов держим в одном месте: их инвалидирует и подписка, и отписка. */
export const friendsQueryKeys = {
  // Фильтр входит в ключ: «только онлайн» — это другой список с сервера, а не подмножество
  // уже загруженного, и класть их в один кеш значит показывать чужие строки при переключении.
  list: (kind: FollowListKind, presenceFilter: string | null = null) =>
    ['follow-list', kind, presenceFilter] as const,
  counts: ['follow-counts'] as const,
  teammates: ['recent-teammates'] as const,
};

const followStatesDoc = graphql(`
  query FollowStates($userIds: [UUID!]!) {
    followStates(userIds: $userIds) {
      userId
      state
    }
  }
`);

export type FollowStateValue = 'NONE' | 'FOLLOWING' | 'FOLLOWED_BY' | 'FRIENDS';

/**
 * Состояния связи со списком людей — один запрос на весь список.
 *
 * Нужен там, где порядок зависит от связи: пикер приглашений ставит друзей выше незнакомцев,
 * и без батча экран из двадцати кандидатов стоил бы двадцати запросов.
 */
export const fetchFollowStates = async (
  userIds: string[]
): Promise<Map<string, FollowStateValue>> => {
  const map = new Map<string, FollowStateValue>();
  if (userIds.length === 0) {
    return map;
  }

  const data = await gqlFetcher(followStatesDoc, { userIds });
  (data?.followStates ?? []).forEach((row) => {
    if (row?.userId && row.state) {
      map.set(row.userId, row.state as FollowStateValue);
    }
  });

  return map;
};
